/*
 * One name per object (UX audit X-A3 V21, G-41). A conversation has three names and the difference
 * between them decides which one a surface may use:
 *
 *   the title    what it is *called*        "Allgemein", "Bea Bergmann"
 *   the address  how a *place* is named     "#allgemein" — the sidebar, the header, the composer,
 *                                           Activity, a thread chip, a search hit, the browse row
 *   the handle   who is on the other side   "@bea", for a direct conversation
 *
 * Before this file the same ladder was written twice — once in the chat module for its own surfaces
 * and once in the Activity feed (`core/activity/feed.ts`) because core may not read a module — and
 * the two had already drifted: one channel was written four ways on four screens. The rule lives
 * here, in core, where every surface can reach it, and `modules/chat/conversation/title.ts` is the
 * chat module's door to it.
 *
 * A channel is `#slug` wherever it is a place, and its display name appears only where the channel
 * is being *described*: the details pane's About and the browse row's second line. A private
 * channel is addressed the same way — the lock beside it is what says it is closed, not its name.
 *
 * Two shapes of the same three answers: the model-level functions, which every place that holds the
 * whole model uses, and the view-level ones for a component that already has the conversation in
 * its hand. The model level is a lookup plus the view level, never a second copy of the rule.
 */

import { formatList } from "../i18n/format.ts"
import type { ConversationId, ConversationView, UserId } from "./model.ts"

/** Display names and handles by account id, as the model holds them. */
export type Directory = Record<UserId, { displayName: string; handle?: string }>

/**
 * The slices of the model a name is resolved against. `Model` satisfies it, and so does Activity's
 * own `ItemLookup` — which is the point: the feed resolves a name without holding a whole model.
 */
export interface NameLookup {
	users: Directory
	conversations: Record<ConversationId, { meta: ConversationView }>
	me: { id: UserId } | null
}

// ---- the model level: what a surface holding the model asks ----

/** What a conversation is called: its display name, or the people a direct one is with. */
export function conversationTitle(model: NameLookup, conv: ConversationId): string {
	const meta = model.conversations[conv]?.meta
	return meta === undefined ? "" : titleOf(meta, model.users, model.me?.id ?? 0)
}

/** How the conversation is named as a *place*: `#slug` for a channel, else its title. */
export function conversationAddress(model: NameLookup, conv: ConversationId): string {
	const meta = model.conversations[conv]?.meta
	return meta === undefined ? "" : addressOf(meta, model.users, model.me?.id ?? 0)
}

/** The handle behind a direct conversation, "@bea"; undefined for everything else. */
export function conversationHandle(model: NameLookup, conv: ConversationId): string | undefined {
	const meta = model.conversations[conv]?.meta
	return meta === undefined ? undefined : handleOf(meta, model.users, model.me?.id ?? 0)
}

// ---- the view level: what a component holding the conversation asks ----

/**
 * The title of one conversation. A channel and a private conversation carry a name on the wire; a
 * direct conversation does not — it is named after the people in it, which only the reader's own
 * model knows. The fallbacks, in order: the people other than the reader, then the `/d/` handle the
 * route is addressed by, then the empty string, which is what a conversation the model has not
 * filled in yet honestly is.
 */
export function titleOf(conv: ConversationView, users: Directory, meId: UserId = 0): string {
	if (conv.name !== undefined && conv.name !== "") return conv.name
	const others = otherMembers(conv, meId)
		.map((id) => users[id]?.displayName ?? "")
		.filter((name) => name !== "")
	if (others.length > 0) return formatList(others)
	return conv.dmHandle ?? ""
}

/**
 * Where a message goes, and how the place is named on every surface that names it: a channel and a
 * private channel are their short name with its hash — which is what the address bar, the composer
 * and everybody in the team say — and everything else is its title.
 */
export function addressOf(conv: ConversationView, users: Directory, meId: UserId = 0): string {
	const named = conv.kind === "channel" || conv.kind === "private"
	if (named && conv.slug !== undefined && conv.slug !== "") return `#${conv.slug}`
	return titleOf(conv, users, meId)
}

/**
 * The handle of the person on the other side of a direct conversation, "@bea" — the second line of
 * identity a header needs and a member count cannot give ("· 2 members" says nothing about *which*
 * two). Undefined for a group, for one's own notes, and while the model knows neither the members
 * nor the address.
 */
export function handleOf(
	conv: ConversationView,
	users: Directory,
	meId: UserId,
): string | undefined {
	if (conv.kind !== "dm") return undefined
	const others = otherMembers(conv, meId)
	const handle = others.length === 1 ? users[others[0] as UserId]?.handle : undefined
	// A direct conversation of two is addressed by the other person's handle, so the route's own
	// segment answers while the member list is still on its way (service_conversation.go dmHandle).
	const fallback = others.length === 0 ? conv.dmHandle : undefined
	const found = handle ?? fallback
	return found === undefined || found === "" ? undefined : `@${found}`
}

/** The members of a conversation other than the reader, in the order the wire listed them. */
function otherMembers(conv: ConversationView, meId: UserId): UserId[] {
	return (conv.memberIds ?? []).filter((id) => id !== meId)
}
